import { useState, useMemo } from "react";
import { T } from "../theme";
import { Button } from "./fmxkit";
import { claudeFetch, parseClaudeText } from "../apiClient";
import { getBaseSchemaType } from "../schemas";
import {
  getActivePrompt, getEnabledExamplesForPrompt,
  createExtractionRun, completeExtractionRun, incrementExampleUsage,
} from "../db";
import { buildSystemPrompt, extractUsage } from "../promptTemplates";

// Add rule / Edit rule — 3-step verified-transform flow (Map step redesign).
//   describe → the user says what should happen in plain language
//   writing  → Claude formalizes it into { rule, code } (admin-editable prompt
//              via the `transform_rule` stage, with inline fallback + run logging)
//   verify   → the user checks the rule text against a live before→after
//              preview of their own values, edits if needed, then applies.
// Applied rules keep the existing shape: { instruction, code } — `code` is a
// JS function body `(row, mapping) => value` executed by buildMappedRows.

const RESPONSE_CONTRACT =
  'Return ONLY a JSON object, no markdown: {"rule": "<one or two plain-language sentences stating exactly what happens to each value — the user verifies this text>", ' +
  '"code": "<JavaScript function body (no function declaration) that receives `row` (object keyed by source column name) and returns the string value for the FMX field>"}';

export default function MapRuleModal({
  fieldName,
  sourceColumn,   // mapped source column for this field, or null
  csv,
  mapping,
  schemaType,
  projectId,
  userId,
  sourceFilename,
  currentRule,    // { instruction, code } — rule already applied on this import (edit flow)
  savedRule,      // { instruction, code } — rule remembered from a previous import
  onSave,         // ({ instruction, code }) => void
  onRemove,       // () => void — clears the applied rule (edit flow only)
  onClose,
}) {
  const editing = !!currentRule;
  const initial = currentRule || savedRule || null;
  const [step, setStep] = useState(initial ? "verify" : "describe");
  const [desc, setDesc] = useState("");
  const [ruleText, setRuleText] = useState(initial?.instruction || "");
  const [code, setCode] = useState(initial?.code || "");
  const [err, setErr] = useState("");

  const columnValues = useMemo(() => {
    if (!csv || !sourceColumn) return [];
    return csv.rows.map(r => r[sourceColumn]).filter(v => v !== undefined && String(v).trim() !== "");
  }, [csv, sourceColumn]);
  const valueCount = sourceColumn ? columnValues.length : (csv?.rows?.length || 0);

  // Live before→after preview: run the generated code against the first rows
  // that actually have a value in the source column.
  const previewRows = useMemo(() => {
    if (!csv?.rows?.length) return [];
    const withValue = sourceColumn ? csv.rows.filter(r => String(r[sourceColumn] ?? "").trim() !== "") : [];
    const base = (withValue.length ? withValue : csv.rows).slice(0, 3);
    let fn = null;
    if (code.trim()) {
      try { fn = new Function("row", "mapping", `"use strict"; ${code}`); } catch { /* preview shows error */ }
    }
    return base.map(row => {
      const before = sourceColumn ? String(row[sourceColumn] ?? "") : "";
      let after = before;
      let failed = false;
      if (fn) {
        try {
          const v = fn(row, mapping || {});
          after = v === null || v === undefined ? "" : String(v);
        } catch { failed = true; }
      } else if (code.trim()) {
        failed = true; // code present but doesn't parse
      }
      return { before, after, failed };
    });
  }, [code, csv, sourceColumn, mapping]);

  const writeRule = async () => {
    if (!desc.trim()) return;
    setStep("writing");
    setErr("");
    const baseType = getBaseSchemaType(schemaType);
    const samples = [...new Set(columnValues)].slice(0, 12);
    const start = Date.now();
    let promptRow = null;
    let examples = [];
    let runId = null;
    try {
      promptRow = await getActivePrompt(baseType, "transform_rule");
      if (promptRow?.id) {
        examples = await getEnabledExamplesForPrompt(promptRow.id);
        const runRow = await createExtractionRun({
          projectId, userId,
          migrationType: baseType,
          stage: "transform_rule",
          sourceFilename: sourceFilename || null,
          pageCount: null,
          promptId: promptRow.id,
          promptVersion: promptRow.version,
        });
        runId = runRow?.id || null;
      }
    } catch { /* non-fatal — fall back to inline prompt */ }

    const facts =
      `FMX field: "${fieldName}". Source column: ${sourceColumn ? `"${sourceColumn}"` : "(not mapped — compute from other columns)"}.\n` +
      `Available columns: ${JSON.stringify(csv?.headers || [])}.\n` +
      `Sample source values: ${JSON.stringify(samples)}.\n` +
      `User's description of the transform: "${desc.trim()}"`;
    const system = promptRow?.body
      ? buildSystemPrompt({
          body: promptRow.body,
          vars: {
            MIGRATION_TYPE: baseType,
            FMX_FIELD: fieldName,
            SOURCE_COLUMN: sourceColumn || "",
            CSV_HEADERS: csv?.headers || [],
            SAMPLE_VALUES: samples,
            INSTRUCTION: desc.trim(),
          },
          examples,
        })
      : `You write data-transform rules for an FMX data-migration tool. The user maps spreadsheet columns to FMX fields and can attach a transform rule to a field. ${RESPONSE_CONTRACT}`;

    try {
      const res = await claudeFetch({
        max_tokens: 800,
        system,
        messages: [{ role: "user", content: `${facts}\n\n${RESPONSE_CONTRACT}` }],
      });
      const clean = parseClaudeText(res) || "{}";
      let parsed = null;
      try { parsed = JSON.parse(clean); } catch { /* handled below */ }
      if (!parsed?.code) throw new Error("Claude returned an unexpected response — try rephrasing your description.");
      setRuleText(parsed.rule || desc.trim());
      setCode(parsed.code);
      setStep("verify");
      if (runId) {
        const usage = extractUsage(res);
        completeExtractionRun(runId, {
          status: "complete",
          resultJson: { field: fieldName, sourceColumn, description: desc.trim(), rule: parsed.rule, code: parsed.code },
          durationMs: Date.now() - start,
          inputTokens: usage.inputTokens,
          outputTokens: usage.outputTokens,
          estimatedCostUsd: usage.costUsd,
        });
        if (examples?.length) incrementExampleUsage(examples.map(e => e.id));
      }
    } catch (e) {
      if (runId) {
        completeExtractionRun(runId, {
          status: "error",
          error: e?.message || String(e),
          durationMs: Date.now() - start,
        });
      }
      setErr(e?.status ? e.message : (e?.message || "Rule generation failed — check your connection."));
      setStep("describe");
    }
  };

  const canApply = !!ruleText.trim() && !!code.trim();
  const label = { font: `700 11px/16px ${T.fontSans}`, textTransform: "uppercase", letterSpacing: "0.07em", color: T.inkSoft };
  const footer = { padding: "12px 18px", borderTop: `1px solid ${T.borderSubtle}`, display: "flex", gap: 8 };

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200, display: "flex", justifyContent: "center", alignItems: "flex-start", padding: "120px 20px 20px", overflowY: "auto" }}
    >
      <div onClick={e => e.stopPropagation()} style={{ width: 560, maxWidth: "100%", background: T.white, borderRadius: T.radiusCard, boxShadow: T.shadowLg, overflow: "hidden" }}>
        {/* Header */}
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${T.borderSubtle}`, display: "flex", alignItems: "center", gap: 10 }}>
          <i className="fa-solid fa-wand-magic-sparkles" style={{ color: T.brand, fontSize: 14 }} />
          <span style={{ font: `700 16px ${T.fontSans}`, color: T.ink }}>{editing ? "Edit rule" : "Add rule"} — {fieldName}</span>
          <button onClick={onClose} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: T.inkMuted, fontSize: 15, padding: 4 }}>
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        {step === "describe" && (
          <>
            <div style={{ padding: 18 }}>
              <div style={{ font: `600 13px ${T.fontSans}`, color: T.ink, marginBottom: 6 }}>
                {sourceColumn ? <>What should happen to "{sourceColumn}" values?</> : <>What should "{fieldName}" contain?</>}
              </div>
              <textarea
                value={desc}
                onChange={e => setDesc(e.target.value)}
                rows={3}
                autoFocus
                placeholder='e.g. "Convert MM/DD/YYYY to FMX date format"'
                style={{ width: "100%", boxSizing: "border-box", resize: "vertical", border: `1px solid ${T.borderStrong}`, borderRadius: T.radiusBtn, padding: "8px 10px", font: `400 14px/20px ${T.fontSans}`, color: T.ink, outline: "none" }}
              />
              <div style={{ font: `400 12px/17px ${T.fontSans}`, color: T.inkMuted, marginTop: 8 }}>
                <i className="fa-solid fa-circle-info" style={{ color: T.brand, marginRight: 6, fontSize: 11 }} />
                Describe it in plain language — Claude writes the rule, then you verify it before it's applied.
              </div>
              {err && <div style={{ font: `400 12px/17px ${T.fontSans}`, color: T.red500, marginTop: 8 }}>{err}</div>}
            </div>
            <div style={{ ...footer, justifyContent: "flex-end" }}>
              <Button variant="neutral" onClick={onClose}>Cancel</Button>
              <Button variant="primary" icon="wand-magic-sparkles" onClick={writeRule} style={desc.trim() ? {} : { opacity: 0.45, cursor: "default" }}>
                Write rule
              </Button>
            </div>
          </>
        )}

        {step === "writing" && (
          <div style={{ padding: "36px 18px", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
            <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: 22, color: T.brand }} />
            <div style={{ font: `600 14px ${T.fontSans}`, color: T.ink }}>Claude is writing the rule…</div>
            <div style={{ font: `400 12px ${T.fontSans}`, color: T.inkMuted }}>
              Testing it against your {valueCount} {sourceColumn ? `"${sourceColumn}"` : "row"} values
            </div>
          </div>
        )}

        {step === "verify" && (
          <>
            <div style={{ padding: 18 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <span style={{ width: 22, height: 22, borderRadius: T.radiusPill, background: T.brand, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <i className="fa-solid fa-wand-magic-sparkles" style={{ fontSize: 10 }} />
                </span>
                <span style={{ font: `400 13px/18px ${T.fontSans}`, color: T.inkMuted }}>
                  Claude wrote this rule — <b style={{ color: T.ink }}>check it does what you expect</b>, and edit it if not.
                </span>
              </div>
              {!editing && savedRule && initial === savedRule && (
                <div style={{ font: `400 12px/17px ${T.fontSans}`, color: T.greenText, marginBottom: 8 }}>
                  <i className="fa-solid fa-clock-rotate-left" style={{ marginRight: 6, fontSize: 10 }} />
                  Saved rule from a previous import — review the preview before applying.
                </div>
              )}
              <textarea
                value={ruleText}
                onChange={e => setRuleText(e.target.value)}
                rows={3}
                style={{ width: "100%", boxSizing: "border-box", resize: "vertical", border: `1px solid ${T.borderStrong}`, borderRadius: T.radiusBtn, padding: "8px 10px", font: `400 13px/19px ${T.fontSans}`, color: T.ink, outline: "none", background: T.blue50 }}
              />
              <div style={{ ...label, margin: "14px 0 6px" }}>
                Preview — first {previewRows.length} of {valueCount} values
              </div>
              <div style={{ border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusBtn, overflow: "hidden" }}>
                {previewRows.length === 0 && (
                  <div style={{ padding: "8px 10px", font: `400 12px ${T.fontSans}`, color: T.inkSoft }}>No sample values to preview.</div>
                )}
                {previewRows.map(({ before, after, failed }, i) => (
                  <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 28px 1fr", alignItems: "center", padding: "6px 10px", borderBottom: i < previewRows.length - 1 ? `1px solid ${T.borderSubtle}` : "none", background: i % 2 === 1 ? T.bg : T.white }}>
                    <span style={{ font: `400 12px ${T.fontData}`, color: T.inkMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{before}</span>
                    <i className="fa-solid fa-arrow-right-long" style={{ fontSize: 10, color: T.inkSoft, textAlign: "center" }} />
                    {failed
                      ? <span style={{ font: `500 12px ${T.fontData}`, color: T.red500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>rule error on this row</span>
                      : <span style={{ font: `500 12px ${T.fontData}`, color: before === after ? T.inkSoft : T.greenDark, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{after}{before === after ? "  (no change)" : ""}</span>}
                  </div>
                ))}
              </div>
            </div>
            <div style={footer}>
              {editing && onRemove && (
                <Button variant="neutral" icon="trash-can" onClick={onRemove} style={{ color: T.red500 }}>Remove rule</Button>
              )}
              {!editing && (
                <Button variant="neutral" icon="arrow-left" onClick={() => setStep("describe")}>Rewrite</Button>
              )}
              <div style={{ flex: 1 }} />
              <Button variant="neutral" onClick={onClose}>Cancel</Button>
              <Button
                variant="primary"
                icon="check"
                onClick={() => canApply && onSave({ instruction: ruleText.trim(), code })}
                style={canApply ? {} : { opacity: 0.45, cursor: "default" }}
              >
                Looks right — apply
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
